<template>
  <UContainer class="py-12">
    <div class="flex items-center justify-between mb-8">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Roadmap</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">GitLab Epics Übersicht</p>
      </div>
      <div class="flex gap-2">
        <UButton color="gray" icon="i-heroicons-arrow-left" to="/" variant="ghost">
          Zurück
        </UButton>
        <UButton
          :loading="loading"
          color="gray"
          icon="i-heroicons-arrow-path"
          variant="ghost"
          @click="fetchEpics"
        >
          Neu laden
        </UButton>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-12">
      <UIcon class="w-8 h-8 animate-spin" name="i-heroicons-arrow-path" />
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <!-- In Umsetzung -->
      <div class="flex flex-col gap-4">
        <div
          class="flex items-center gap-2 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded text-blue-700 dark:text-blue-300 font-semibold"
        >
          <UIcon name="i-heroicons-play-circle" />
          In Umsetzung
          <UBadge class="ml-auto" color="blue" size="xs" variant="subtle">{{
            inUmsetzung.length
          }}</UBadge>
        </div>
        <div class="flex flex-col gap-3">
          <EpicCard v-for="epic in inUmsetzung" :key="epic.id" :epic="epic" />
          <div
            v-if="inUmsetzung.length === 0"
            class="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg text-gray-400 text-sm"
          >
            Keine Epics
          </div>
        </div>
      </div>

      <!-- Geplant -->
      <div class="flex flex-col gap-4">
        <div
          class="flex items-center gap-2 px-2 py-1 bg-green-50 dark:bg-green-900/20 rounded text-green-700 dark:text-green-300 font-semibold"
        >
          <UIcon name="i-heroicons-calendar" />
          Geplant
          <UBadge class="ml-auto" color="green" size="xs" variant="subtle">{{
            geplant.length
          }}</UBadge>
        </div>
        <div class="flex flex-col gap-3">
          <EpicCard v-for="epic in geplant" :key="epic.id" :epic="epic" />
          <div
            v-if="geplant.length === 0"
            class="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg text-gray-400 text-sm"
          >
            Keine Epics
          </div>
        </div>
      </div>

      <!-- Zukunft -->
      <div class="flex flex-col gap-4">
        <div
          class="flex items-center gap-2 px-2 py-1 bg-purple-50 dark:bg-purple-900/20 rounded text-purple-700 dark:text-purple-300 font-semibold"
        >
          <UIcon name="i-heroicons-clock" />
          Zukunft
          <UBadge class="ml-auto" color="purple" size="xs" variant="subtle">{{
            zukunft.length
          }}</UBadge>
        </div>
        <div class="flex flex-col gap-3">
          <EpicCard v-for="epic in zukunft" :key="epic.id" :epic="epic" />
          <div
            v-if="zukunft.length === 0"
            class="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg text-gray-400 text-sm"
          >
            Keine Epics
          </div>
        </div>
      </div>
    </div>
  </UContainer>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import EpicCard from '@/components/EpicCard.vue'

const toast = useToast()

interface GitlabEpic {
  id: number
  iid: number
  title: string
  description: string
  web_url: string
  labels: string[]
}

const epics = ref<GitlabEpic[]>([])
const loading = ref(false)

const fetchEpics = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/registry/v1/gitlab/epics')
    epics.value = response.data
  } catch (error) {
    console.error('Failed to fetch epics', error)
    toast.add({
      title: 'Fehler',
      description: 'Die Roadmap konnte nicht geladen werden.',
      color: 'danger',
    })
  } finally {
    loading.value = false
  }
}

const getPriority = (epic: GitlabEpic): number => {
  const labels = epic.labels.map((l) => l.toLowerCase())
  if (labels.includes('priority::high')) return 1
  if (labels.includes('priority::normal')) return 2
  return 3 // Default to low
}

const sortEpics = (a: GitlabEpic, b: GitlabEpic) => {
  const prioA = getPriority(a)
  const prioB = getPriority(b)
  if (prioA !== prioB) return prioA - prioB
  return a.title.localeCompare(b.title)
}

const inUmsetzung = computed(() =>
  epics.value
    .filter((e) => e.labels.some((l) => l.toLowerCase() === 'epic::progress'))
    .sort(sortEpics),
)

const geplant = computed(() =>
  epics.value
    .filter((e) => e.labels.some((l) => l.toLowerCase() === 'epic::planned'))
    .sort(sortEpics),
)

const zukunft = computed(() =>
  epics.value
    .filter((e) => {
      const labels = e.labels.map((l) => l.toLowerCase())
      const hasState =
        labels.includes('epic::progress') ||
        labels.includes('epic::planned') ||
        labels.includes('epic::future')
      return labels.includes('epic::future') || !hasState
    })
    .sort(sortEpics),
)

onMounted(() => {
  fetchEpics()
})
</script>
